<script setup lang="ts">
import type { Activity } from '../../../model/assessments/interfaces'

import { AppText } from '@shared/ui/typography'

import { usePreviewActivity } from '../../../model/assessments/preview-activity.hook'
import PreviewAuthorNote from './PreviewAuthorNote.vue'
import PreviewContentBlocks from './PreviewContentBlocks.vue'

/**
 * One activity: its prompt (content blocks), the interaction a kid would face, and the things a
 * kid never sees — points, rubric references and standards, each inside a `PreviewAuthorNote`.
 * The interaction is dispatched through `INTERACTION_PREVIEWS`. No interaction is a legitimate
 * prompt-only activity, not an error, and says so — or a Tutor observation, when it carries rubrics.
 */
defineOptions({
  name: 'PreviewActivity',
})

const props = defineProps<{
  activity: Activity
  position: number
}>()

const {
  refContent,
  refHasContent,
  refInteraction,
  refInteractionPreview,
  refIsUnknownInteraction,
  refIsTutorObservation,
  refAuthorNote,
  refRubrics,
  refHasRubrics,
  refStandards,
  refHasStandards,
} = usePreviewActivity(() => props.activity)
</script>

<template>
  <div class="preview-activity">
    <AppText
      name="body2-medium"
      color="slate-500"
    >
      Activity {{ position }}
    </AppText>

    <PreviewContentBlocks
      v-if="refHasContent"
      :blocks="refContent"
    />

    <component
      :is="refInteractionPreview"
      v-if="refInteractionPreview && refInteraction"
      :interaction="refInteraction"
    />

    <!-- A type this build has no preview for: a document written by a newer editor. Named, not
         swallowed — silence would look like an activity with nothing in it. -->
    <AppText
      v-else-if="refIsUnknownInteraction"
      name="body2"
      color="slate-500"
    >
      This activity uses an interaction this editor cannot preview.
    </AppText>

    <AppText
      v-else-if="refIsTutorObservation"
      name="body2"
      color="slate-500"
    >
      The tutor scores this live with its rubrics.
    </AppText>

    <AppText
      v-else
      name="body2"
      color="slate-500"
    >
      Nothing for a kid to answer here — this activity has no interaction.
    </AppText>

    <PreviewAuthorNote v-if="refAuthorNote">
      {{ refAuthorNote }}
    </PreviewAuthorNote>

    <!-- Rubrics are REFERENCED by id (decision B8): resolved against the catalog, and a
         dangling id is shown as an unresolved reference rather than skipped, so the preview
         agrees with what validation reports. -->
    <PreviewAuthorNote v-if="refHasRubrics">
      reviewed against
      <ul class="preview-activity__rubrics">
        <li
          v-for="rubric in refRubrics"
          :key="rubric.key"
        >
          <template v-if="rubric.isMissing">
            {{ rubric.id }} — not in the rubric catalog
          </template>
          <template v-else>
            {{ rubric.name }}
          </template>
        </li>
      </ul>
    </PreviewAuthorNote>

    <!-- Every tagged standard, not just the first. Only the code: the document stores codes, and
         the statement would have to be fetched from the catalogue to show here. -->
    <PreviewAuthorNote v-if="refHasStandards">
      aligned to
      <ul class="preview-activity__standards">
        <li
          v-for="standard in refStandards"
          :key="standard.key"
        >
          {{ standard.code }}
        </li>
      </ul>
    </PreviewAuthorNote>
  </div>
</template>

<style scoped lang="scss" src="./PreviewActivity.scss" />
